import { clockLabel } from "../sim/time";
import { ITEM_NAME } from "../sim/inventory";
import { strikePct } from "../sim/combat";
import type { WorldState } from "../sim/types";
import type { Atlas } from "./assets";
import { C } from "./palette";

export function drawHud(
  ctx: CanvasRenderingContext2D,
  w: WorldState,
  atlas: Atlas,
  vw: number,
  vh: number,
): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  if (w.mode === "title") {
    drawTitle(ctx, w, atlas, vw, vh);
    ctx.restore();
    return;
  }

  const panelHud = atlas.ui.panel;
  const px = 12;
  const py = 10;
  const pw = 256;
  const ph = 132;
  if (panelHud) ctx.drawImage(panelHud, px - 4, py - 4, pw, ph);

  const bx = 36;
  bar(ctx, bx, 38, 212, 14, w.player.hp / w.player.hpMax, "#2a1410", "#c44a32");
  bar(ctx, bx, 58, 212, 10, w.player.stamina, "#1a2418", "#7a9a58");
  bar(ctx, bx, 74, 212, 8, 1 - w.player.hunger, "#2a2010", C.bone);
  bar(ctx, bx, 90, 118, 6, strikePct(w), "#2a2418", C.lantern);

  // Text sits below bars, inside panel safe area (1280×720 QA: no overlap with frame)
  ctx.fillStyle = "rgba(8,6,5,0.55)";
  ctx.fillRect(bx - 4, 102, 220, 34);
  ctx.fillStyle = C.bone;
  ctx.font = "600 12px ui-sans-serif, system-ui";
  ctx.fillText(`${clockLabel(w.timeSec)}   ${w.player.x.toFixed(0)},${w.player.y.toFixed(0)}`, bx, 116);
  ctx.fillStyle = C.dry;
  ctx.font = "11px ui-sans-serif, system-ui";
  ctx.fillText(`${w.season} · ${w.weather}${w.mode === "hollow" && w.hollow ? ` · depth ${w.hollow.depth}` : ""}`, bx, 130);

  const slots = Math.min(8, Math.max(w.bag.length, 4));
  for (let i = 0; i < slots; i++) {
    const x = 24 + i * 52;
    const y = vh - 72;
    const slot = atlas.ui.slot;
    if (slot) ctx.drawImage(slot, x, y, 46, 46);
    else {
      ctx.fillStyle = "rgba(20,16,12,0.85)";
      ctx.strokeStyle = C.rust;
      ctx.lineWidth = 2;
      ctx.fillRect(x, y, 46, 46);
      ctx.strokeRect(x, y, 46, 46);
    }
    const it = w.bag[i];
    if (it) {
      const ic = atlas.icons[it.id];
      if (ic) ctx.drawImage(ic, x + 5, y + 3, 36, 36);
      ctx.fillStyle = C.lantern;
      ctx.font = "700 11px ui-sans-serif, system-ui";
      ctx.fillText(String(it.n), x + 32, y + 42);
    }
  }

  if (w.msgT > 0) {
    const panel = atlas.ui.panel;
    const pw = Math.min(640, vw - 48);
    const ph = 78;
    const px = (vw - pw) / 2;
    const py = vh - 170;
    ctx.globalAlpha = Math.min(1, w.msgT / 0.35);
    if (panel) ctx.drawImage(panel, px, py, pw, ph);
    else {
      ctx.fillStyle = "rgba(12,10,9,0.88)";
      ctx.fillRect(px, py, pw, ph);
    }
    ctx.fillStyle = C.lantern;
    ctx.font = "500 16px ui-sans-serif, system-ui";
    wrap(ctx, w.msg, px + 28, py + 32, pw - 56, 20);
    ctx.globalAlpha = 1;
  }

  if (w.mode === "pause") {
    ctx.fillStyle = "rgba(8,6,5,0.72)";
    ctx.fillRect(0, 0, vw, vh);
    ctx.fillStyle = C.lantern;
    ctx.font = "700 28px ui-sans-serif, system-ui";
    ctx.fillText("STILL", 48, 80);
    ctx.fillStyle = C.bone;
    ctx.font = "16px ui-sans-serif, system-ui";
    ctx.fillText("Esc resume   ·   the well does not pause", 48, 112);
    ctx.fillText(`Deaths ${w.deaths}   extracts ${w.extracts}   deepest ${w.knowledge.deepest}`, 48, 140);
  }

  if (w.mode === "death") {
    ctx.fillStyle = "rgba(10,4,4,0.82)";
    ctx.fillRect(0, 0, vw, vh);
    ctx.fillStyle = C.blood;
    ctx.font = "700 34px ui-sans-serif, system-ui";
    ctx.fillText("YOU DID NOT COME BACK", 48, vh / 2 - 20);
    ctx.fillStyle = C.bone;
    ctx.font = "18px ui-sans-serif, system-ui";
    ctx.fillText("The bag stays where you fell. Enter to wake on the porch.", 48, vh / 2 + 20);
  }

  const hint = "WASD move  ·  Shift dash  ·  J/click strike  ·  E use  ·  F lantern  ·  Esc";
  const hintY = vh - 12;
  ctx.fillStyle = "rgba(8,6,5,0.78)";
  ctx.fillRect(0, vh - 30, vw, 30);
  ctx.fillStyle = C.lantern;
  ctx.font = "600 12px ui-sans-serif, system-ui";
  ctx.shadowColor = "rgba(0,0,0,0.85)";
  ctx.shadowBlur = 4;
  ctx.fillText(hint, 24, hintY);
  ctx.shadowBlur = 0;
  ctx.restore();
}

function drawTitle(ctx: CanvasRenderingContext2D, _w: WorldState, atlas: Atlas, vw: number, vh: number): void {
  if (atlas.title) {
    const img = atlas.title;
    const s = Math.max(vw / img.width, vh / img.height);
    ctx.drawImage(img, (vw - img.width * s) / 2, (vh - img.height * s) / 2, img.width * s, img.height * s);
  } else {
    ctx.fillStyle = C.coal;
    ctx.fillRect(0, 0, vw, vh);
  }
  ctx.fillStyle = "rgba(8,6,5,0.45)";
  ctx.fillRect(0, 0, vw, vh);
  ctx.fillStyle = C.lantern;
  ctx.font = "800 56px ui-sans-serif, system-ui";
  ctx.fillText("CINDERWELL", 48, vh - 140);
  ctx.fillStyle = C.bone;
  ctx.font = "18px ui-sans-serif, system-ui";
  ctx.fillText("A town around a wound.", 52, vh - 104);
  ctx.fillText("Enter to wake.", 52, vh - 76);
}

function bar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  t: number,
  back: string,
  fill: string,
): void {
  ctx.fillStyle = back;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = fill;
  ctx.fillRect(x + 1, y + 1, Math.max(0, (w - 2) * Math.max(0, Math.min(1, t))), h - 2);
  ctx.strokeStyle = "rgba(196,165,116,0.35)";
  ctx.strokeRect(x, y, w, h);
}

function wrap(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, max: number, lh: number): void {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > max) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lh;
    } else line = test;
  }
  if (line) ctx.fillText(line, x, yy);
}

export function inspectItem(w: WorldState, i: number): string {
  const it = w.bag[i];
  return it ? ITEM_NAME[it.id] : "";
}
